// src/modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/modals/CorrectionLetterModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Typography, Alert, Input, Form, Button, Space, Select, Divider } from 'antd';
import {
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import appService from 'services/appService';
import translateX from 'utils/translateX';

const { Text, Paragraph } = Typography;

// *******************************************************************************
// HELPER - Download XML file
// *******************************************************************************
const downloadXml = (xmlContent, fileName) => {
  if (!xmlContent) return;

  const blob = new Blob([xmlContent], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'carta_correcao.xml';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// *******************************************************************************
// HELPER - Download from URL (fetch as blob to force download)
// *******************************************************************************
const downloadFromUrl = async (url, fileName) => {
  if (!url) return;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'carta_correcao.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('[CorrectionLetterModal] Failed to download from URL:', err);
    window.open(url, '_blank');
  }
};

// *******************************************************************************
// SEFAZ GROUPS - Grupos válidos para correção
// *******************************************************************************
const SEFAZ_GROUPS = [
  { value: 'dest', labelKey: 'sefaz_group_dest' },
  { value: 'emit', labelKey: 'sefaz_group_emit' },
  { value: 'prod', labelKey: 'sefaz_group_prod' },
  { value: 'transp', labelKey: 'sefaz_group_transp' },
  { value: 'cobr', labelKey: 'sefaz_group_cobr' },
  { value: 'infAdic', labelKey: 'sefaz_group_infAdic' },
];

// *******************************************************************************
// SEFAZ FIELDS BY GROUP
// *******************************************************************************
const SEFAZ_FIELDS_BY_GROUP = {
  dest: [
    { value: 'xNome', label: 'Nome/Razão Social (xNome)' },
    { value: 'CNPJ', label: 'CNPJ' },
    { value: 'CPF', label: 'CPF' },
    { value: 'IE', label: 'Inscrição Estadual (IE)' },
    { value: 'email', label: 'E-mail (email)' },
    { value: 'xLgr', label: 'Logradouro (xLgr)' },
    { value: 'nro', label: 'Número (nro)' },
    { value: 'xCpl', label: 'Complemento (xCpl)' },
    { value: 'xBairro', label: 'Bairro (xBairro)' },
    { value: 'cMun', label: 'Cód. Município IBGE (cMun)' },
    { value: 'xMun', label: 'Município (xMun)' },
    { value: 'UF', label: 'UF' },
    { value: 'CEP', label: 'CEP' },
    { value: 'cPais', label: 'Cód. País (cPais)' },
    { value: 'xPais', label: 'País (xPais)' },
    { value: 'fone', label: 'Telefone (fone)' },
    { value: 'indIEDest', label: 'Ind. Inscrição Estadual (indIEDest)' },
  ],
  emit: [
    { value: 'xNome', label: 'Nome/Razão Social (xNome)' },
    { value: 'xFant', label: 'Nome Fantasia (xFant)' },
    { value: 'IE', label: 'Inscrição Estadual (IE)' },
    { value: 'IEST', label: 'IE Subs. Trib. (IEST)' },
    { value: 'IM', label: 'Inscrição Municipal (IM)' },
    { value: 'CNAE', label: 'CNAE' },
    { value: 'CRT', label: 'Regime Tributário (CRT)' },
    { value: 'xLgr', label: 'Logradouro (xLgr)' },
    { value: 'nro', label: 'Número (nro)' },
    { value: 'xCpl', label: 'Complemento (xCpl)' },
    { value: 'xBairro', label: 'Bairro (xBairro)' },
    { value: 'cMun', label: 'Cód. Município IBGE (cMun)' },
    { value: 'xMun', label: 'Município (xMun)' },
    { value: 'UF', label: 'UF' },
    { value: 'CEP', label: 'CEP' },
    { value: 'cPais', label: 'Cód. País (cPais)' },
    { value: 'xPais', label: 'País (xPais)' },
    { value: 'fone', label: 'Telefone (fone)' },
  ],
  prod: [
    { value: 'cProd', label: 'Código (cProd)' },
    { value: 'cEAN', label: 'EAN (cEAN)' },
    { value: 'xProd', label: 'Descrição (xProd)' },
    { value: 'NCM', label: 'NCM' },
    { value: 'CEST', label: 'CEST' },
    { value: 'cBenef', label: 'Cód. Benefício (cBenef)' },
    { value: 'EXTIPI', label: 'EX TIPI' },
    { value: 'CFOP', label: 'CFOP' },
    { value: 'uCom', label: 'Unidade Comercial (uCom)' },
    { value: 'xPed', label: 'Pedido (xPed)' },
    { value: 'nItemPed', label: 'Item Pedido (nItemPed)' },
    { value: 'nFCI', label: 'FCI (nFCI)' },
  ],
  transp: [
    { value: 'modFrete', label: 'Modalidade (modFrete)' },
    { value: 'CNPJ', label: 'CNPJ Transitador' },
    { value: 'CPF', label: 'CPF Transitador' },
    { value: 'xNome', label: 'Nome Transportador (xNome)' },
    { value: 'IE', label: 'IE Transportador' },
    { value: 'xEnder', label: 'Endereço (xEnder)' },
    { value: 'xMun', label: 'Município (xMun)' },
    { value: 'UF', label: 'UF' },
    { value: 'qVol', label: 'Qtd. Volumes (qVol)' },
    { value: 'esp', label: 'Espécie (esp)' },
    { value: 'marca', label: 'Marca' },
    { value: 'nVol', label: 'Num. Volumes (nVol)' },
    { value: 'pesoL', label: 'Peso Líquido (pesoL)' },
    { value: 'pesoB', label: 'Peso Bruto (pesoB)' },
  ],
  cobr: [
    { value: 'nFat', label: 'Num. Fatura (nFat)' },
    { value: 'nDup', label: 'Num. Duplicata (nDup)' },
    { value: 'dVenc', label: 'Data Vencimento (dVenc)' },
  ],
  infAdic: [
    { value: 'infAdFisco', label: 'Inf. Fisco (infAdFisco)' },
    { value: 'infCpl', label: 'Inf. Complementar (infCpl)' },
  ],
};

// *******************************************************************************
// ERROR VIEW COMPONENT
// *******************************************************************************
const CorrectionErrorView = ({ resultData }) => {
  const { error, code, xml, xmlStorageUrl } = resultData || {};

  const handleDownloadXml = () => {
    const fileName = 'carta_correcao_rejeitada.xml';

    if (xmlStorageUrl) {
      downloadFromUrl(xmlStorageUrl, fileName);
    } else if (xml) {
      downloadXml(xml, fileName);
    }
  };

  const hasDownloadOption = xmlStorageUrl || xml;

  return (
    <div style={{ paddingTop: 8 }}>
      <Alert
        type="error"
        showIcon
        icon={<CloseCircleOutlined />}
        message={translateX('correction_letter_error_title')}
        description={error || translateX('correction_letter_error')}
        style={{ marginBottom: 16 }}
      />

      {code && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('error_code')}: </Text>
          <Text strong style={{ fontSize: 14 }}>
            {code}
          </Text>
        </div>
      )}

      <Divider />

      {hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {translateX('correction_letter_download_rejection_info')}
          </Paragraph>
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadXml}>
              {translateX('download')}
            </Button>
          </Space>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// RESULT VIEW COMPONENT
// *******************************************************************************
const CorrectionResultView = ({ resultData, invoiceNumber }) => {
  const { protocolNumber, cceId, eventDate, invoiceId, corrections, xml, xmlStorageUrl } =
    resultData || {};

  const displayNumber = invoiceNumber || invoiceId || '-';

  const handleDownloadXml = () => {
    const fileName = protocolNumber ? `carta_correcao_${protocolNumber}.xml` : 'carta_correcao.xml';

    if (xmlStorageUrl) {
      downloadFromUrl(xmlStorageUrl, fileName);
    } else if (xml) {
      downloadXml(xml, fileName);
    }
  };

  const hasDownloadOption = xmlStorageUrl || xml;

  return (
    <div style={{ paddingTop: 8 }}>
      <Alert
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
        message={translateX('correction_letter_success_title')}
        description={translateX('correction_letter_success_description')}
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{translateX('invoice_number')}: </Text>
        <Text strong style={{ fontSize: 14 }}>
          {displayNumber}
        </Text>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{translateX('protocol_number')}: </Text>
        <Text strong style={{ fontSize: 16 }}>
          {protocolNumber || '-'}
        </Text>
      </div>

      {cceId && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('cce_id')}: </Text>
          <Text strong>{cceId}</Text>
        </div>
      )}

      {eventDate && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('event_date')}: </Text>
          <Text strong>{new Date(eventDate).toLocaleString('pt-BR')}</Text>
        </div>
      )}

      {corrections && corrections.length > 0 && (
        <>
          <Divider />
          <Text type="secondary" strong>
            {translateX('corrections_made')}:
          </Text>
          {corrections.map((c, idx) => (
            <div key={idx} style={{ marginTop: 8, paddingLeft: 16 }}>
              <Text type="secondary">
                {c.grupo_alterado}.{c.campo_alterado}: <Text strong>{c.valor_alterado}</Text>
              </Text>
            </div>
          ))}
        </>
      )}

      <Divider />

      {hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {translateX('correction_letter_download_info')}
          </Paragraph>
          <Space>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadXml}>
              {translateX('download')}
            </Button>
          </Space>
        </div>
      )}

      {!hasDownloadOption && (
        <div style={{ marginTop: 16 }}>
          <Space>
            <FileTextOutlined style={{ color: '#8c8c8c' }} />
            <Text type="secondary">{translateX('xml_not_available')}</Text>
          </Space>
        </div>
      )}
    </div>
  );
};

// *******************************************************************************
// CORRECTION ITEM COMPONENT
// *******************************************************************************
const CorrectionItem = ({ field, index, onRemove }) => {
  const form = Form.useFormInstance();
  const selectedGroup = Form.useWatch(['corrections', field.name, 'grupo_alterado'], form);
  const availableOptions = selectedGroup ? SEFAZ_FIELDS_BY_GROUP[selectedGroup] || [] : [];
  const isFieldDisabled = !selectedGroup || availableOptions.length === 0;

  return (
    <div style={{ marginBottom: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
      <div
        style={{
          marginBottom: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text strong>
          {translateX('correction')} #{index + 1}
        </Text>
        {onRemove && (
          <Button type="text" danger icon={<DeleteOutlined />} onClick={onRemove} size="small" />
        )}
      </div>

      <Form.Item
        name={[field.name, 'grupo_alterado']}
        label={translateX('correction_group')}
        rules={[{ required: true, message: translateX('field_required') }]}
      >
        <Select
          placeholder={translateX('select_group')}
          onChange={() => {
            const vals = form.getFieldsValue();
            if (vals.corrections && vals.corrections[field.name]) {
              vals.corrections[field.name].campo_alterado = undefined;
              form.setFieldsValue({ corrections: vals.corrections });
            }
          }}
        >
          {SEFAZ_GROUPS.map(g => (
            <Select.Option key={g.value} value={g.value}>
              {translateX(g.labelKey)}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name={[field.name, 'campo_alterado']}
        label={translateX('correction_field')}
        rules={[{ required: true, message: translateX('field_required') || 'Campo obrigatório' }]}
      >
        <Select
          placeholder={translateX('select_field_placeholder') || 'Selecione o campo'}
          disabled={isFieldDisabled}
          showSearch
        >
          {availableOptions.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name={[field.name, 'valor_alterado']}
        label={translateX('correction_value')}
        rules={[{ required: true, message: translateX('field_required') }]}
      >
        <Input placeholder={translateX('correction_value_placeholder')} />
      </Form.Item>
    </div>
  );
};

// *******************************************************************************
// FORM VIEW COMPONENT
// *******************************************************************************
const CorrectionFormView = ({ company, invoiceId, invoiceNumber, isNfce }) => {
  if (isNfce) {
    return (
      <div style={{ paddingTop: 8 }}>
        <Alert
          type="warning"
          showIcon
          message="Disponível apenas para NF-e"
          description="A carta de correção está disponível apenas para Nota Fiscal Eletrônica (NF-e). Para NFC-e, utilize o cancelamento dentro do prazo de 24 horas."
          style={{ marginBottom: 16 }}
        />
      </div>
    );
  }

  const isInvoiceIdProvided = !!invoiceId;
  const displayNumber = invoiceNumber || invoiceId;

  return (
    <div style={{ paddingTop: 8 }}>


      <Alert
        type="info"
        showIcon
        icon={<EditOutlined />}
        message={translateX('correction_letter_info_title')}
        description={translateX('correction_letter_info_description')}
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{translateX('company')}: </Text>
        <Text strong>{company?.name || company?.fullName || '-'}</Text>
      </div>

      {isInvoiceIdProvided && displayNumber && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{translateX('invoice_number')}: </Text>
          <Text strong style={{ fontSize: 14 }}>
            {displayNumber}
          </Text>
        </div>
      )}

      <Form.Item
        name="invoiceId"
        label={translateX('invoice_id')}
        rules={[{ required: true, message: translateX('field_required') }]}
        initialValue={invoiceId}
        hidden
      >
        <Input />
      </Form.Item>



      <Divider />

      <div style={{ marginBottom: 16 }}>
        <Text strong>{translateX('corrections')}</Text>
      </div>

      <Form.List name="corrections">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <CorrectionItem
                key={field.key}
                field={field}
                index={index}
                onRemove={fields.length > 1 ? () => remove(field.name) : null}
              />
            ))}
            <Button
              type="dashed"
              onClick={() => add({ grupo_alterado: '', campo_alterado: '', valor_alterado: '' })}
              icon={<PlusOutlined />}
              block
              style={{ marginBottom: 16 }}
            >
              {translateX('add_correction')}
            </Button>
          </>
        )}
      </Form.List>


    </div>
  );
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CorrectionLetterModalStepContent = ({
  company,
  invoiceId,
  invoiceNumber,
  resultData,
  showResult,
  isNfce,
}) => {
  if (showResult && resultData) {
    if (resultData.success === false) {
      return <CorrectionErrorView resultData={resultData} />;
    }
    return <CorrectionResultView resultData={resultData} invoiceNumber={invoiceNumber} />;
  }
  return (
    <CorrectionFormView company={company} invoiceId={invoiceId} invoiceNumber={invoiceNumber} isNfce={isNfce} />
  );
};

// *******************************************************************************
// STEP CONFIG
// *******************************************************************************
const correctionLetterModalStep = ({
  company,
  invoiceId,
  invoiceNumber,
  resultData,
  showResult,
  isNfce,
}) => {
  return {
    stepTitle: showResult ? 'result' : 'correction_letter',
    stepTooltip: showResult ? 'correction_result_tooltip' : 'correction_letter_tooltip',
    content: () => (
      <CorrectionLetterModalStepContent
        company={company}
        invoiceId={invoiceId}
        invoiceNumber={invoiceNumber}
        resultData={resultData}
        showResult={showResult}
        isNfce={isNfce}
      />
    ),
    validationFields: showResult || isNfce ? [] : ['invoiceId', 'corrections'],
    initialValues: {
      invoiceId: invoiceId || undefined,
      corrections: [{ grupo_alterado: '', campo_alterado: '', valor_alterado: '' }],
    },
    onNext: async ({ form }) => {
      if (showResult || isNfce) {
        return true;
      }

      const values = form?.getFieldsValue(true) || {};
      const finalInvoiceId = invoiceId || values.invoiceId;

      if (!finalInvoiceId) {
        form.setFields([
          {
            name: 'invoiceId',
            errors: [translateX('field_required')],
          },
        ]);
        return false;
      }

      const corrections = values.corrections || [];
      if (!corrections || corrections.length === 0) {
        appService.message({
          flag: 'warning',
          text: 'add_at_least_one_correction',
          duration: 3,
        });
        return false;
      }

      // Validate each correction has all fields
      const invalidCorrections = corrections.filter(
        c => !c?.grupo_alterado || !c?.campo_alterado || !c?.valor_alterado
      );
      if (invalidCorrections.length > 0) {
        appService.message({
          flag: 'warning',
          text: 'fill_all_correction_fields',
          duration: 3,
        });
        return false;
      }

      return true;
    },
  };
};

export default correctionLetterModalStep;
